import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { protectSheetWithRandomPassword } from './ExcelUtils';

const InvestmentReviewExcelExport = async ({ data, meta, loginPlaceId }) => {
    if (!Array.isArray(data) || data.length === 0) return;

    const cleanedData = data.map(item => ({
        ...item,
        branchName: (item.branchName || "").trim(),
        bankName: (item.bankName || "").trim(),
        tbill: Number(item.tbill || 0),
        fixed: Number(item.fixed || 0),
        other: Number(item.other || 0),
        total: Number(item.total || 0)
    }));

    const branchDataMap = {};
    cleanedData.forEach(item => {
        if (item.branchName) {
            if (!branchDataMap[item.branchName]) {
                branchDataMap[item.branchName] = [];
            }
            branchDataMap[item.branchName].push(item);
        }
    });

    const branches = [...new Set(cleanedData.map(d => d.branchName))]
        .filter(branch => branch && branch !== "")
        .sort();

    if (branches.length === 0) return;

    const knownBanksOrder = [
        "Bank Of Ceylon",
        "NSB",
        "Seylan Bank",
        "Sampath Bank",
        "Commercial Bank",
        "HNB",
        "Peoples Bank",
        "HSBC",
        "Regional Development Bank (RDB)",
        "HDFC",
        "SMIB"
    ];

    const workbook = new ExcelJS.Workbook();
    workbook.creator = 'Loan Management System';
    workbook.created = new Date();

    const sheet = workbook.addWorksheet("Investment Review");

    const investmentTypes = [
        { label: "Treasury Bills", key: "tbill" },
        { label: "Fixed Deposits", key: "fixed" },
        { label: "Other Investments", key: "other" },
        { label: "Total Investment", key: "total" },
        { label: "Percentage (%)", key: "percentage" }
    ];

    const totalColumns = 2 + (branches.length * 5);
    const amountFormat = "#,##0.00";
    const percentFormat = "0.00%";
    let currentRow = 1;

    sheet.mergeCells(currentRow, 1, currentRow, totalColumns);
    const titleCell = sheet.getCell(currentRow, 1);
    titleCell.value = "INVESTMENT REVIEW REPORT";
    titleCell.font = { bold: true, size: 18, color: { argb: "000080" } };
    titleCell.alignment = { horizontal: "center", vertical: "middle" };
    sheet.getRow(currentRow).height = 35;
    currentRow++;

    sheet.mergeCells(currentRow, 1, currentRow, totalColumns);
    const districtCell = sheet.getCell(currentRow, 1);
    districtCell.value = `District: ${meta.districtName || "All Districts"}`;
    districtCell.font = { bold: true, size: 14 };
    districtCell.alignment = { horizontal: "left", vertical: "middle" };
    sheet.getRow(currentRow).height = 25;
    currentRow++;

    sheet.mergeCells(currentRow, 1, currentRow, totalColumns);
    const divisionalCell = sheet.getCell(currentRow, 1);
    divisionalCell.value = `Divisional Areas: ${meta.divisionalName || "All Areas"}`;
    divisionalCell.font = { bold: true, size: 14 };
    divisionalCell.alignment = { horizontal: "left", vertical: "middle" };
    sheet.getRow(currentRow).height = 25;
    currentRow++;

    sheet.mergeCells(currentRow, 1, currentRow, totalColumns);
    const periodCell = sheet.getCell(currentRow, 1);
    periodCell.value = `Period: ${meta.startDate} to ${meta.endDate}`;
    periodCell.font = { bold: true, size: 14 };
    periodCell.alignment = { horizontal: "center", vertical: "middle" };
    sheet.getRow(currentRow).height = 25;
    currentRow++;

    currentRow++;

    sheet.getCell(currentRow, 1).value = "No";
    sheet.getCell(currentRow, 1).font = { bold: true };
    sheet.getCell(currentRow, 1).alignment = { horizontal: "center", vertical: "middle" };
    sheet.getCell(currentRow, 1).fill = { type: "pattern", pattern: "solid", fgColor: { argb: "E6F3FF" } };

    sheet.getCell(currentRow, 2).value = "Invested Bank / Institution";
    sheet.getCell(currentRow, 2).font = { bold: true };
    sheet.getCell(currentRow, 2).alignment = { horizontal: "center", vertical: "middle" };
    sheet.getCell(currentRow, 2).fill = { type: "pattern", pattern: "solid", fgColor: { argb: "E6F3FF" } };

    let colIndex = 3;
    branches.forEach(branch => {
        sheet.mergeCells(currentRow, colIndex, currentRow, colIndex + 4);
        const branchHeaderCell = sheet.getCell(currentRow, colIndex);
        branchHeaderCell.value = branch.toUpperCase();
        branchHeaderCell.font = { bold: true, size: 12, color: { argb: "0000FF" } };
        branchHeaderCell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "E6F3FF" } };
        branchHeaderCell.alignment = { horizontal: "center", vertical: "middle" };

        investmentTypes.forEach((type, index) => {
            const subHeaderCell = sheet.getCell(currentRow + 1, colIndex + index);
            subHeaderCell.value = type.label;
            subHeaderCell.font = { bold: true };
            subHeaderCell.alignment = { horizontal: "center", vertical: "middle" };
            subHeaderCell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "D9E1F2" } };
        });

        colIndex += 5;
    });

    sheet.getCell(currentRow + 1, 1).value = "";
    sheet.getCell(currentRow + 1, 1).fill = { type: "pattern", pattern: "solid", fgColor: { argb: "D9E1F2" } };

    sheet.getCell(currentRow + 1, 2).value = "";
    sheet.getCell(currentRow + 1, 2).fill = { type: "pattern", pattern: "solid", fgColor: { argb: "D9E1F2" } };

    currentRow += 2;

    knownBanksOrder.forEach((bankName, index) => {
        const serialCell = sheet.getCell(currentRow, 1);
        serialCell.value = index + 1;
        serialCell.alignment = { horizontal: "center", vertical: "middle" };

        const bankCell = sheet.getCell(currentRow, 2);
        bankCell.value = bankName;
        bankCell.alignment = { horizontal: "left", vertical: "middle" };

        let dataColIndex = 3;

        branches.forEach(branch => {
            const branchItems = branchDataMap[branch] || [];
            const bankData = branchItems.find(item => item.bankName === bankName) || {};

            const tbill = Number(bankData.tbill || 0);
            const fixed = Number(bankData.fixed || 0);
            const other = Number(bankData.other || 0);
            const total = Number(bankData.total || 0);

            const tbillCell = sheet.getCell(currentRow, dataColIndex);
            tbillCell.value = tbill;
            tbillCell.numFmt = amountFormat;
            tbillCell.alignment = { horizontal: "right", vertical: "middle" };

            const fixedCell = sheet.getCell(currentRow, dataColIndex + 1);
            fixedCell.value = fixed;
            fixedCell.numFmt = amountFormat;
            fixedCell.alignment = { horizontal: "right", vertical: "middle" };

            const otherCell = sheet.getCell(currentRow, dataColIndex + 2);
            otherCell.value = other;
            otherCell.numFmt = amountFormat;
            otherCell.alignment = { horizontal: "right", vertical: "middle" };

            const totalCell = sheet.getCell(currentRow, dataColIndex + 3);
            totalCell.value = total;
            totalCell.numFmt = amountFormat;
            totalCell.alignment = { horizontal: "right", vertical: "middle" };
            totalCell.font = { bold: true };

            const percentCell = sheet.getCell(currentRow, dataColIndex + 4);
            percentCell.value = 0;
            percentCell.numFmt = percentFormat;
            percentCell.alignment = { horizontal: "right", vertical: "middle" };

            dataColIndex += 5;
        });

        currentRow++;
    });

    currentRow++;

    sheet.mergeCells(currentRow, 1, currentRow, 2);
    const grandTotalLabel = sheet.getCell(currentRow, 1);
    grandTotalLabel.value = "GRAND TOTAL";
    grandTotalLabel.font = { bold: true };
    grandTotalLabel.alignment = { horizontal: "right", vertical: "middle" };
    grandTotalLabel.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFFFCC" } };

    let totalColIndex = 3;

    branches.forEach(branch => {
        const branchItems = branchDataMap[branch] || [];

        const totalTbill = branchItems.reduce((sum, item) => sum + Number(item.tbill || 0), 0);
        const totalFixed = branchItems.reduce((sum, item) => sum + Number(item.fixed || 0), 0);
        const totalOther = branchItems.reduce((sum, item) => sum + Number(item.other || 0), 0);
        const totalInvestment = branchItems.reduce((sum, item) => sum + Number(item.total || 0), 0);

        const tbillTotalCell = sheet.getCell(currentRow, totalColIndex);
        tbillTotalCell.value = totalTbill;
        tbillTotalCell.numFmt = amountFormat;
        tbillTotalCell.font = { bold: true };
        tbillTotalCell.alignment = { horizontal: "right", vertical: "middle" };

        const fixedTotalCell = sheet.getCell(currentRow, totalColIndex + 1);
        fixedTotalCell.value = totalFixed;
        fixedTotalCell.numFmt = amountFormat;
        fixedTotalCell.font = { bold: true };
        fixedTotalCell.alignment = { horizontal: "right", vertical: "middle" };

        const otherTotalCell = sheet.getCell(currentRow, totalColIndex + 2);
        otherTotalCell.value = totalOther;
        otherTotalCell.numFmt = amountFormat;
        otherTotalCell.font = { bold: true };
        otherTotalCell.alignment = { horizontal: "right", vertical: "middle" };

        const totalInvestmentCell = sheet.getCell(currentRow, totalColIndex + 3);
        totalInvestmentCell.value = totalInvestment;
        totalInvestmentCell.numFmt = amountFormat;
        totalInvestmentCell.font = { bold: true };
        totalInvestmentCell.alignment = { horizontal: "right", vertical: "middle" };

        const percentTotalCell = sheet.getCell(currentRow, totalColIndex + 4);
        percentTotalCell.value = 1;
        percentTotalCell.numFmt = percentFormat;
        percentTotalCell.font = { bold: true };
        percentTotalCell.alignment = { horizontal: "right", vertical: "middle" };

        for (let i = 0; i < 5; i++) {
            const cell = sheet.getCell(currentRow, totalColIndex + i);
            cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFFFCC" } };
        }

        totalColIndex += 5;
    });

    currentRow++;

    branches.forEach((branch, branchIndex) => {
        const totalColForBranch = 3 + (branchIndex * 5) + 3;
        const percentColForBranch = totalColForBranch + 1;
        const grandTotalCellAddress = sheet.getCell(currentRow - 1, totalColForBranch).address;

        const startDataRow = 6;
        for (let bankRow = startDataRow; bankRow < currentRow - 1; bankRow++) {
            const totalCell = sheet.getCell(bankRow, totalColForBranch);
            const percentCell = sheet.getCell(bankRow, percentColForBranch);

            if (totalCell.value > 0) {
                percentCell.value = { formula: `${totalCell.address}/${grandTotalCellAddress}` };
            }
            percentCell.numFmt = percentFormat;
        }
    });

    for (let row = 1; row <= currentRow; row++) {
        for (let col = 1; col <= totalColumns; col++) {
            const cell = sheet.getCell(row, col);
            if (!cell.border) {
                cell.border = {
                    top: { style: "thin" },
                    bottom: { style: "thin" },
                    left: { style: "thin" },
                    right: { style: "thin" }
                };
            }
        }
    }

    sheet.getColumn(1).width = 8;
    sheet.getColumn(2).width = 40;

    let widthColIndex = 3;
    branches.forEach(() => {
        sheet.getColumn(widthColIndex).width = 22;
        sheet.getColumn(widthColIndex + 1).width = 22;
        sheet.getColumn(widthColIndex + 2).width = 22;
        sheet.getColumn(widthColIndex + 3).width = 22;
        sheet.getColumn(widthColIndex + 4).width = 18;
        widthColIndex += 5;
    });

    sheet.getRow(1).height = 35;
    sheet.getRow(2).height = 25;
    sheet.getRow(3).height = 25;
    sheet.getRow(4).height = 25;
    sheet.getRow(6).height = 30;
    sheet.getRow(7).height = 30;

    for (let row = 8; row <= currentRow; row++) {
        sheet.getRow(row).height = 24;
    }

    if (loginPlaceId !== "1") {
        const password = protectSheetWithRandomPassword(sheet, 12);
    }

    sheet.views = [{ state: "frozen", xSplit: 2, ySplit: 7 }];

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
    });

    const fileName = `Investment_Review_${meta.startDate.replace(/\//g, '-')}_to_${meta.endDate.replace(/\//g, '-')}.xlsx`;
    saveAs(blob, fileName);
};

export default InvestmentReviewExcelExport;